/**
 * Gobodo relationship type keys for Account/Client-related traversals.
 * Aligns with setup-gobodo-account-relationships (Unlock_Request→Client, Unlock_Request→Case, CreditReport→Client,
 * Case→Client, direct activity→Client edges, Case-side activity edges, Case→Record Type, Case→User assignee).
 * Used by Credit Block history, credit report, case, and activity traversal queries.
 */

import { getCustomSuffix } from './gobodoConfig';

const suffix = () => getCustomSuffix();

/** Relationship type key for Unlock_Request → Client (RDS custom_unlock_requests). */
export function getUnlockRequestClientRelKey(): string {
  return `unlock_request_client_rel${suffix()}`;
}

/** Relationship type key for Unlock_Request → Case (CaseRelField on UR, UnlockRequestRelField on Case). */
export function getUnlockRequestCaseRelKey(): string {
  return `unlock_request_case_rel${suffix()}`;
}

/** Relationship type key for CreditReport → Client (RDS custom_credit_reports). */
export function getCreditReportClientRelKey(): string {
  return `credit_report_client_rel${suffix()}`;
}

/** Relationship type key for Task → Client (RDS tasks.account_id / custom__client / related_to_id). */
export function getTaskClientRelKey(): string {
  return `task_client_rel${suffix()}`;
}

/** Relationship type key for Event → Client (RDS events.account_id / custom__client / related_to_id). */
export function getEventClientRelKey(): string {
  return `event_client_rel${suffix()}`;
}

/** Relationship type key for EmailMessage → Client (RDS email_message.custom__client / account_id / related_to_id). */
export function getEmailMessageClientRelKey(): string {
  return `email_message_client_rel${suffix()}`;
}

/** Relationship type key for Note → Client (RDS notes.custom__client / account_id / related_to_id). */
export function getNoteClientRelKey(): string {
  return `note_client_rel${suffix()}`;
}

/** Relationship type key for generic Activity → parent object (UI-created Activity records). */
export function getActivityRelKey(): string {
  return `activity_rel${suffix()}`;
}

/** Relationship type key for Case → Client (RDS cases.account_id). */
export function getCaseClientRelKey(): string {
  return `case_client_rel${suffix()}`;
}

/** Relationship type key for Case → Record Type (RDS record_type_id / RecordTypeId__c; inverse traversal from Record Type INCOMING → Case). */
export function getRecordTypeRelKey(): string {
  return `record_type_rel${suffix()}`;
}

/** Relationship type key for Case → User assignee (Credit Block OwnerRelField / case_assignee_rel). */
export function getCaseAssigneeRelKey(): string {
  return `case_assignee_rel${suffix()}`;
}

/** Relationship type key for generic object → User owner traversal on non-Case objects. */
export function getOwnerRelKey(): string {
  return `owner_rel${suffix()}`;
}

/** Relationship type key for Task → Case (RDS tasks.what_id). */
export function getTaskCaseRelKey(): string {
  return `task_case_rel${suffix()}`;
}

/** Relationship type key for Event → Case (RDS events.what_id). */
export function getEventCaseRelKey(): string {
  return `event_case_rel${suffix()}`;
}

/** Relationship type key for EmailMessage → Case (RDS email_message.parent_id). */
export function getCaseEmailRelKey(): string {
  return `case_email_rel${suffix()}`;
}

/** Relationship type key for CaseHistory → Case (RDS case_history.parent_id). */
export function getCaseHistoryRelKey(): string {
  return `case_history_rel${suffix()}`;
}

/** Relationship type key for CaseFeed → Case (RDS case_feed.parent_id). */
export function getCaseFeedRelKey(): string {
  return `case_feed_rel${suffix()}`;
}

/** Relationship type key for Note → Case (RDS notes.parent_id / custom__case / related_to_id). */
export function getNoteCaseRelKey(): string {
  return `note_case_rel${suffix()}`;
}

/** Relationship type key for CSAudit → Client (CyberSweep). */
export function getCSAuditClientRelKey(): string {
  return `cs_audit_client_rel${suffix()}`;
}

/**
 * Relationship type key for CSAudit → User (auditor).
 * Graph direction: CSAudit is the start object, portal USER (or resolved user type) is the end.
 * Traverse from the auditor user with direction INCOMING to list assigned audits in one hop.
 */
export function getCSAuditAuditorRelKey(): string {
  return `cs_audit_auditor_rel${suffix()}`;
}

/** Relationship type key for CSAudit → CyberSweep audit template (CyberSweep). */
export function getCSAuditTemplateRelKey(): string {
  return `cs_audit_template_rel${suffix()}`;
}

/** Relationship type key for PAWRecord → CSAudit (CyberSweep). */
export function getPAWCSAuditRelKey(): string {
  return `paw_cs_audit_rel${suffix()}`;
}

/** Relationship type key for PAWRecord → CyberSweep audit website (CyberSweep). */
export function getPAWAuditWebsiteRelKey(): string {
  return `paw_audit_website_rel${suffix()}`;
}
